import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    deleteField,
    query,
    where,
    onSnapshot,
    writeBatch
} from 'firebase/firestore';
import {
    signOut as firebaseSignOut
} from 'firebase/auth';
import { db, auth } from './config';
import { User, UserNotification } from '../../types';

// ==================== USERS ====================

/**
 * Create a new user in Firestore
 */
export const createUser = async (user: User): Promise<void> => {
    try {
        await setDoc(doc(db, 'users', user.id), user);
        console.log('User created successfully:', user.id);
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

/**
 * Get a user by ID
 */
export const getUser = async (userId: string): Promise<User | null> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            return userDoc.data() as User;
        }
        return null;
    } catch (error) {
        console.error('Error getting user:', error);
        throw error;
    }
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async (): Promise<User[]> => {
    try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        return usersSnapshot.docs.map(doc => doc.data() as User);
    } catch (error) {
        console.error('Error getting all users:', error);
        throw error;
    }
};

/**
 * Sanitize an object by removing undefined values (Firestore doesn't accept undefined)
 * and converting undefined to deleteField() where needed
 */
const sanitizeForFirestore = (obj: any): any => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(item => sanitizeForFirestore(item)).filter(item => item !== null && item !== undefined);

    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value === undefined) {
            // Skip undefined values - Firestore does not accept undefined
            continue;
        } else if (Array.isArray(value)) {
            // Recursively sanitize arrays (handles arrays of objects with undefined fields)
            sanitized[key] = value.map(item => sanitizeForFirestore(item)).filter(item => item !== null && item !== undefined);
        } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
            // Recursively sanitize nested objects
            const nested = sanitizeForFirestore(value);
            if (Object.keys(nested).length > 0) {
                sanitized[key] = nested;
            }
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
};

/**
 * Update a user
 */
export const updateUser = async (userId: string, updates: Partial<User>): Promise<void> => {
    try {
        // Guard against undefined/null updates
        if (!updates || typeof updates !== 'object') {
            console.log('No valid updates object provided for user:', userId);
            return;
        }

        // Sanitize updates to remove undefined values
        const sanitizedUpdates = sanitizeForFirestore(updates);

        // Handle explicit deletions (when activePromoEffects should be removed)
        // Check for both null and undefined as signals to delete
        if (updates && 'activePromoEffects' in updates &&
            (updates.activePromoEffects === undefined || updates.activePromoEffects === null)) {
            sanitizedUpdates.activePromoEffects = deleteField();
        }

        if (Object.keys(sanitizedUpdates).length === 0) {
            console.log('No valid updates to apply for user:', userId);
            return;
        }

        await updateDoc(doc(db, 'users', userId), sanitizedUpdates);
        console.log('User updated successfully:', userId);
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

/**
 * Delete a user
 */
export const deleteUser = async (userId: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, 'users', userId));
        console.log('User deleted successfully:', userId);
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

/**
 * Get user by username (for login)
 */
export const getUserByUsername = async (username: string): Promise<User | null> => {
    try {
        console.log('[Firebase] getUserByUsername called with username:', username);
        console.log('[Firebase] Platform:', typeof (window as any).Capacitor !== 'undefined' ? 'APK/Native' : 'Web');
        console.log('[Firebase] Firestore instance:', db ? 'Initialized' : 'NOT INITIALIZED');

        const q = query(collection(db, 'users'), where('username', '==', username));
        console.log('[Firebase] Query created, executing...');

        const querySnapshot = await getDocs(q);
        console.log('[Firebase] Query executed. Empty?', querySnapshot.empty);
        console.log('[Firebase] Docs found:', querySnapshot.docs.length);

        if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data() as User;
            console.log('[Firebase] User found:', userData.id, userData.username);
            return userData;
        }

        console.log('[Firebase] No user found for username:', username);
        return null;
    } catch (error: any) {
        console.error('[Firebase] Error getting user by username:', error);
        console.error('[Firebase] Error name:', error?.name);
        console.error('[Firebase] Error code:', error?.code);
        console.error('[Firebase] Error message:', error?.message);
        throw error;
    }
};

/**
 * Get user by email (for Google login)
 */
export const getUserByEmail = async (email: string): Promise<User | null> => {
    try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].data() as User;
        }
        return null;
    } catch (error) {
        console.error('Error getting user by email:', error);
        throw error;
    }
};

/**
 * Get user by Google ID
 */
export const getUserByGoogleId = async (googleId: string): Promise<User | null> => {
    try {
        // First check by ID pattern
        const userDoc = await getDoc(doc(db, 'users', `google-${googleId}`));
        if (userDoc.exists()) {
            return userDoc.data() as User;
        }

        // Also check googleId field
        const q = query(collection(db, 'users'), where('googleId', '==', googleId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].data() as User;
        }
        return null;
    } catch (error) {
        console.error('Error getting user by Google ID:', error);
        throw error;
    }
};

/**
 * Get user by Facebook ID
 */
export const getUserByFacebookId = async (facebookId: string): Promise<User | null> => {
    try {
        // First check by ID pattern
        const userDoc = await getDoc(doc(db, 'users', `facebook-${facebookId}`));
        if (userDoc.exists()) {
            return userDoc.data() as User;
        }

        // Also check facebookId field
        const q = query(collection(db, 'users'), where('facebookId', '==', facebookId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].data() as User;
        }
        return null;
    } catch (error) {
        console.error('Error getting user by Facebook ID:', error);
        throw error;
    }
};

// ==================== DEVICE REGISTRATION TRACKING ====================

interface DeviceRegistration {
    id: string;
    fingerprint: string;
    ipAddress: string;
    userAgent: string;
    registeredUsers: string[];
    registrationCount: number;
    firstRegistration: number;
    lastRegistration: number;
    blocked: boolean;
    blockReason?: string;
}

interface IPRegistration {
    id: string;
    ipAddress: string;
    registeredUsers: string[];
    registrationCount: number;
    firstRegistration: number;
    lastRegistration: number;
    blocked: boolean;
    blockReason?: string;
}

/**
 * Get device registration by fingerprint
 */
export const getDeviceRegistration = async (fingerprint: string): Promise<DeviceRegistration | null> => {
    try {
        const docRef = doc(db, 'deviceRegistrations', fingerprint);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as DeviceRegistration;
        }
        return null;
    } catch (error) {
        console.error('Error getting device registration:', error);
        return null;
    }
};

/**
 * Get IP registration
 */
export const getIPRegistration = async (ipAddress: string): Promise<IPRegistration | null> => {
    try {
        // Encode IP address for use as document ID
        const encodedIP = ipAddress.replace(/\./g, '_').replace(/:/g, '-');
        const docRef = doc(db, 'ipRegistrations', encodedIP);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as IPRegistration;
        }
        return null;
    } catch (error) {
        console.error('Error getting IP registration:', error);
        return null;
    }
};

/**
 * Save or update device registration
 */
export const saveDeviceRegistration = async (registration: DeviceRegistration): Promise<void> => {
    try {
        await setDoc(doc(db, 'deviceRegistrations', registration.id), registration);
        console.log('Device registration saved:', registration.id);
    } catch (error) {
        console.error('Error saving device registration:', error);
        throw error;
    }
};

/**
 * Save or update IP registration
 */
export const saveIPRegistration = async (registration: IPRegistration): Promise<void> => {
    try {
        // Encode IP address for use as document ID
        const encodedIP = registration.ipAddress.replace(/\./g, '_').replace(/:/g, '-');
        await setDoc(doc(db, 'ipRegistrations', encodedIP), { ...registration, id: encodedIP });
        console.log('IP registration saved:', registration.ipAddress);
    } catch (error) {
        console.error('Error saving IP registration:', error);
        throw error;
    }
};

/**
 * Record a new user registration from a device/IP
 */
export const recordUserRegistration = async (
    fingerprint: string,
    ipAddress: string,
    userAgent: string,
    userId: string
): Promise<void> => {
    try {
        // Update device registration
        const existingDevice = await getDeviceRegistration(fingerprint);

        if (existingDevice) {
            // Update existing record
            const updatedDevice: DeviceRegistration = {
                ...existingDevice,
                registeredUsers: [...existingDevice.registeredUsers, userId],
                registrationCount: existingDevice.registrationCount + 1,
                lastRegistration: Date.now(),
                ipAddress, // Update to latest IP
                userAgent // Update to latest user agent
            };
            await saveDeviceRegistration(updatedDevice);
        } else {
            // Create new record
            const newDevice: DeviceRegistration = {
                id: fingerprint,
                fingerprint,
                ipAddress,
                userAgent,
                registeredUsers: [userId],
                registrationCount: 1,
                firstRegistration: Date.now(),
                lastRegistration: Date.now(),
                blocked: false
            };
            await saveDeviceRegistration(newDevice);
        }

        // Update IP registration (skip if unknown)
        if (ipAddress && ipAddress !== 'unknown') {
            const existingIP = await getIPRegistration(ipAddress);

            if (existingIP) {
                const updatedIP: IPRegistration = {
                    ...existingIP,
                    registeredUsers: [...existingIP.registeredUsers, userId],
                    registrationCount: existingIP.registrationCount + 1,
                    lastRegistration: Date.now()
                };
                await saveIPRegistration(updatedIP);
            } else {
                const newIP: IPRegistration = {
                    id: ipAddress,
                    ipAddress,
                    registeredUsers: [userId],
                    registrationCount: 1,
                    firstRegistration: Date.now(),
                    lastRegistration: Date.now(),
                    blocked: false
                };
                await saveIPRegistration(newIP);
            }
        }
    } catch (error) {
        console.error('Error recording user registration:', error);
        // Don't throw - registration tracking shouldn't block user creation
    }
};

/**
 * Block a device from further registrations
 */
export const blockDevice = async (fingerprint: string, reason: string): Promise<void> => {
    try {
        const existing = await getDeviceRegistration(fingerprint);
        if (existing) {
            await saveDeviceRegistration({
                ...existing,
                blocked: true,
                blockReason: reason
            });
        }
    } catch (error) {
        console.error('Error blocking device:', error);
        throw error;
    }
};

/**
 * Block an IP from further registrations
 */
export const blockIP = async (ipAddress: string, reason: string): Promise<void> => {
    try {
        const existing = await getIPRegistration(ipAddress);
        if (existing) {
            await saveIPRegistration({
                ...existing,
                blocked: true,
                blockReason: reason
            });
        }
    } catch (error) {
        console.error('Error blocking IP:', error);
        throw error;
    }
};

/**
 * Unblock a device
 */
export const unblockDevice = async (fingerprint: string): Promise<void> => {
    try {
        const existing = await getDeviceRegistration(fingerprint);
        if (existing) {
            await saveDeviceRegistration({
                ...existing,
                blocked: false,
                blockReason: undefined
            });
        }
    } catch (error) {
        console.error('Error unblocking device:', error);
        throw error;
    }
};

/**
 * Get all device registrations (admin)
 */
export const getAllDeviceRegistrations = async (): Promise<DeviceRegistration[]> => {
    try {
        const snapshot = await getDocs(collection(db, 'deviceRegistrations'));
        return snapshot.docs.map(doc => doc.data() as DeviceRegistration);
    } catch (error) {
        console.error('Error getting all device registrations:', error);
        return [];
    }
};

/**
 * Get all IP registrations (admin)
 */
export const getAllIPRegistrations = async (): Promise<IPRegistration[]> => {
    try {
        const snapshot = await getDocs(collection(db, 'ipRegistrations'));
        return snapshot.docs.map(doc => doc.data() as IPRegistration);
    } catch (error) {
        console.error('Error getting all IP registrations:', error);
        return [];
    }
};

/**
 * Listen to device registrations in real-time (admin)
 */
export const listenToDeviceRegistrations = (
    callback: (registrations: DeviceRegistration[]) => void
): (() => void) => {
    try {
        const q = collection(db, 'deviceRegistrations');

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const registrations = snapshot.docs.map(doc => doc.data() as DeviceRegistration);
            // Sort by last registration date (newest first)
            registrations.sort((a, b) => b.lastRegistration - a.lastRegistration);
            callback(registrations);
        }, (error) => {
            console.error('Error listening to device registrations:', error);
            callback([]);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Error setting up device registration listener:', error);
        return () => { };
    }
};

// ==================== REAL-TIME LISTENERS ====================

/**
 * Listen to user changes
 */
export const listenToUser = (userId: string, callback: (user: User | null) => void) => {
    return onSnapshot(doc(db, 'users', userId), (doc) => {
        if (doc.exists()) {
            callback(doc.data() as User);
        } else {
            callback(null);
        }
    });
};

/**
 * Listen to all users (Admin)
 */
export const listenToAllUsers = (callback: (users: User[]) => void) => {
    return onSnapshot(collection(db, 'users'), (snapshot) => {
        const users = snapshot.docs.map(doc => doc.data() as User);
        callback(users);
    });
};

/**
 * Create or reset a test user for debugging
 * Username: testuser / Password: test123
 */
export const createTestUser = async (): Promise<User | null> => {
    try {
        const { hashPassword } = await import('../../utils/passwordUtils');

        // Check if test user exists
        const existing = await getUserByUsername('testuser');
        if (existing) {
            console.log('[Firebase] Test user already exists, updating password...');
            const hashedPassword = await hashPassword('test123');
            await updateUser(existing.id, { password: hashedPassword });
            return { ...existing, password: hashedPassword };
        }

        // Create new test user
        const hashedPassword = await hashPassword('test123');
        const testUser: User = {
            id: 'test-user-' + Date.now(),
            name: 'Test User',
            username: 'testuser',
            password: hashedPassword,
            firstName: 'Test',
            lastName: 'User',
            email: 'test@test.com',
            role: 'user',
            accountType: 'standard',
            isPremium: false,
            tokens: 10000,
            maxTokens: 10000,
            totalXP: 0,
            level: 1,
            streak: 0,
            createdAt: new Date().toISOString(),
            dailyMessageCount: 0,
            dailyTokenUsage: 0,
            dailyAttachmentCount: 0,
            lastMessageDate: new Date().toISOString(),
            lastResetTime: Date.now(),
            lastActive: Date.now(),
            tokenBalance: 10000
        };

        await createUser(testUser);
        console.log('[Firebase] Test user created successfully');
        return testUser;
    } catch (error) {
        console.error('[Firebase] Error creating test user:', error);
        return null;
    }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
    await firebaseSignOut(auth);
};

// ==================== NOTIFICATIONS ====================

/**
 * Mark a notification as read for a user
 */
export const markNotificationAsRead = async (userId: string, notificationId: string): Promise<void> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return;

        const userData = userDoc.data() as User;
        const notifications = userData.notifications || [];

        const updatedNotifications = notifications.map(n =>
            n.id === notificationId ? { ...n, read: true } : n
        );

        await updateDoc(doc(db, 'users', userId), { notifications: updatedNotifications });
    } catch (error) {
        console.error('[Firebase] Error marking notification as read:', error);
    }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return;

        const userData = userDoc.data() as User;
        const notifications = userData.notifications || [];

        const updatedNotifications = notifications.map(n => ({ ...n, read: true }));

        await updateDoc(doc(db, 'users', userId), { notifications: updatedNotifications });
    } catch (error) {
        console.error('[Firebase] Error marking all notifications as read:', error);
    }
};

/**
 * Add a notification to a specific user
 */
export const addUserNotification = async (userId: string, notification: Omit<UserNotification, 'id' | 'timestamp' | 'read'>): Promise<void> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return;

        const userData = userDoc.data() as User;
        const existingNotifications = userData.notifications || [];

        const newNotification: UserNotification = {
            ...notification,
            id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: Date.now(),
            read: false
        };

        // Limit to 50 notifications
        const updatedNotifications = [newNotification, ...existingNotifications].slice(0, 50);

        await updateDoc(doc(db, 'users', userId), { notifications: updatedNotifications });
    } catch (error) {
        console.error('[Firebase] Error adding user notification:', error);
    }
};
